import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Twitter, Send, MapPin, Phone, Mail } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useSettings } from "@/context/SettingsContext";

export default function Footer() {
  const s = useSettings();
  const phone1Href = `tel:${(s.phone_1 || "").replace(/[\s+]/g, "")}`;
  const phone2Href = `tel:${(s.phone_2 || "").replace(/[\s+]/g, "")}`;

  return (
    <footer className="relative border-t border-white/10 bg-[#050507] mt-24">
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full overflow-hidden border border-[#D4AF37]/40 bg-black">
              <img src={BRAND.logoUrl} alt="Vedacosmos" className="w-full h-full object-cover"/>
            </div>
            <div>
              <div className="font-serif text-lg text-[#EAE6DF]">{s.brand_name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">
                Ancient Wisdom · Universal Truth · Divine Connection
              </div>
            </div>
          </div>
          <p className="text-sm text-[#A0A4AB] leading-relaxed max-w-md">
            A modern gateway to Vedic wisdom — astrology, kundali, remedies, poojas & sacred products,
            all in one super app.
          </p>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-start gap-3 text-[#A0A4AB]">
              <MapPin size={15} className="text-[#D4AF37] mt-0.5 flex-none"/>
              <div>
                <div className="text-[#EAE6DF]">{s.address_line1}</div>
                <div className="text-xs">{s.address_line2}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={15} className="text-[#D4AF37] flex-none"/>
              <div className="text-[#A0A4AB]">
                <a href={phone1Href} data-testid="footer-phone-1" className="hover:text-[#EAE6DF] transition">{s.phone_1}</a>
                <span className="mx-2 text-[#6E737D]">·</span>
                <a href={phone2Href} data-testid="footer-phone-2" className="hover:text-[#EAE6DF] transition">{s.phone_2}</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={15} className="text-[#D4AF37] flex-none"/>
              <a href={`mailto:${s.email}`} data-testid="footer-email" className="text-[#A0A4AB] hover:text-[#EAE6DF] transition">{s.email}</a>
            </div>
            <a
              href={s.waLink()}
              target="_blank" rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 text-sm hover:bg-emerald-500/20 transition"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Chat on WhatsApp
            </a>
          </div>

          {/* App download links — always visible */}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-2">Download the App</div>
            <div className="flex gap-2 flex-wrap">
              {s.android_url ? (
                <a href={s.android_url} target="_blank" rel="noopener noreferrer" data-testid="footer-android"
                   className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:border-[#D4AF37] transition text-xs text-[#EAE6DF]">
                  <span className="text-lg text-[#D4AF37]">▶</span>
                  <div className="leading-none text-left">
                    <div className="text-[9px] text-[#A0A4AB]">Get it on</div>
                    <div>Google Play</div>
                  </div>
                </a>
              ) : (
                <div data-testid="footer-android-soon" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 opacity-60 text-xs">
                  <span className="text-lg text-[#D4AF37]">▶</span>
                  <div className="leading-none text-left">
                    <div className="text-[9px] text-[#A0A4AB]">Android app</div>
                    <div className="text-[#A0A4AB]">Coming soon</div>
                  </div>
                </div>
              )}
              {s.ios_url ? (
                <a href={s.ios_url} target="_blank" rel="noopener noreferrer" data-testid="footer-ios"
                   className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:border-[#D4AF37] transition text-xs text-[#EAE6DF]">
                  <span className="text-lg text-[#D4AF37]"></span>
                  <div className="leading-none text-left">
                    <div className="text-[9px] text-[#A0A4AB]">Download on</div>
                    <div>App Store</div>
                  </div>
                </a>
              ) : (
                <div data-testid="footer-ios-soon" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 opacity-60 text-xs">
                  <span className="text-lg text-[#D4AF37]"></span>
                  <div className="leading-none text-left">
                    <div className="text-[9px] text-[#A0A4AB]">iOS app</div>
                    <div className="text-[#A0A4AB]">Coming soon</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            {[
              { Ic: Instagram, url: s.instagram_url },
              { Ic: Youtube, url: s.youtube_url },
              { Ic: Twitter, url: s.twitter_url },
              { Ic: Send, url: s.telegram_url },
            ].filter(x => x.url).map(({ Ic, url }, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-[#A0A4AB] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors">
                <Ic size={14} strokeWidth={1.5}/>
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Services</div>
          <ul className="space-y-2 text-sm text-[#A0A4AB]">
            <li><Link to="/services/astrology" className="hover:text-[#EAE6DF]">Astrology</Link></li>
            <li><Link to="/services/numerology" className="hover:text-[#EAE6DF]">Numerology</Link></li>
            <li><Link to="/services/tarot" className="hover:text-[#EAE6DF]">Tarot</Link></li>
            <li><Link to="/services/vastu" className="hover:text-[#EAE6DF]">Vastu</Link></li>
            <li><Link to="/services/yantra-mantra-tantra" className="hover:text-[#EAE6DF]">Yantra-Mantra-Tantra</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-[#A0A4AB]">
            <li><Link to="/kundali" className="hover:text-[#EAE6DF]">Auto AI Kundali</Link></li>
            <li><Link to="/astrologers" className="hover:text-[#EAE6DF]">Book Astrologer</Link></li>
            <li><Link to="/shop" className="hover:text-[#EAE6DF]">Spiritual Store</Link></li>
            <li><Link to="/live" className="hover:text-[#EAE6DF]">Live Pooja & Darshan</Link></li>
            <li><Link to="/learn" className="hover:text-[#EAE6DF]">Academy (LMS)</Link></li>
            <li><Link to="/membership" className="hover:text-[#EAE6DF]">Membership</Link></li>
            <li><Link to="/blogs" className="hover:text-[#EAE6DF]">Blogs</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Company</div>
          <ul className="space-y-2 text-sm text-[#A0A4AB]">
            <li><a href="#" className="hover:text-[#EAE6DF]">About</a></li>
            <li><a href="#" className="hover:text-[#EAE6DF]">Contact</a></li>
            <li><a href="#" className="hover:text-[#EAE6DF]">Careers</a></li>
            <li><a href="#" className="hover:text-[#EAE6DF]">Refund Policy</a></li>
            <li><a href="#" className="hover:text-[#EAE6DF]">Terms & Privacy</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#6E737D]">
          <div>© {new Date().getFullYear()} {s.brand_name} · {s.address_line1}, {s.address_line2}</div>
          <div className="font-deva">वेदकॉसमोस — प्राचीन ज्ञान, आधुनिक तकनीक</div>
        </div>
      </div>
    </footer>
  );
}
